/**
 * Dynamic (per-partner) auth provider factory.
 *
 * Used when multi-tenant partner authentication is enabled
 * (`VITE_USE_DYNAMIC_PARTNER_AUTH="true"`). Each partner can have its own
 * Azure AD app registration (client ID, authority, scopes), resolved at
 * startup via the `/partners/resolve` backend endpoint.
 *
 * Unlike the static singleton in authProviderInstance.ts, this creates a
 * **new** MSAL auth provider for every partner configuration, so different
 * partner subdomains can authenticate against different Azure AD tenants.
 *
 * @see authProviderInstance.ts — static fallback when dynamic auth is off
 * @see msalAuthProvider.ts     — underlying MSAL implementation
 */
import { AuthProvider } from "react-admin";
import { createMsalAuthProvider } from "./msalAuthProvider";
import { mockAuthProvider } from "./mockAuthProvider";
import { USE_AUTH } from "@/config/auth";
import { PartnerAuthConfig, extractScopes } from "@/services/partnerResolution";

// Cache the provider by clientId+authority so React Strict Mode's double-invoked
// useEffect reuses the same MSAL instance instead of creating a competing one.
// Two MSAL instances race over handleRedirectPromise() — the loser gets null,
// causing checkAuth to reject and forcing a second sign-in.
let cachedProvider: AuthProvider | null = null;
let cachedKey: string | null = null;

export const createDynamicAuthProvider = (
  config: PartnerAuthConfig,
  options?: { apiBaseUrl?: string }
): AuthProvider => {
  if (!USE_AUTH) {
    console.log("[DynamicAuthProvider] Auth disabled, using mock provider");
    return mockAuthProvider;
  }

  if (!config.clientId || !config.authority) {
    throw new Error("[DynamicAuthProvider] clientId and authority are required when auth is enabled");
  }

  // Return cached provider if config hasn't changed
  const key = `${config.clientId}|${config.authority}`;
  if (cachedProvider && cachedKey === key) {
    console.log("[DynamicAuthProvider] Returning cached auth provider");
    return cachedProvider;
  }

  console.log("[DynamicAuthProvider] Creating auth provider with config:", {
    clientId: config.clientId,
    authority: config.authority,
    mode: config.mode,
  });

  const scopes = extractScopes(config.scope);

  cachedProvider = createMsalAuthProvider({
    clientId: config.clientId,
    authority: config.authority,
    redirectUri: `${window.location.origin}/`,
    scopes,
    loginMethod: import.meta.env.VITE_MSAL_LOGIN_METHOD || "redirect",
    useOIDC: !!config.scope,
    apiBaseUrl: options?.apiBaseUrl || config.apiBaseUrl,
  });
  cachedKey = key;

  return cachedProvider;
};
